
import { AWSFileService } from "@/services/aws-file-service";
import { MessagesService } from "@/services/messages-service";
import { IMail, ISendMessageDialogContact, splitMails } from "@/utils/mail";
import { DossierConfigService } from "@/services/dossier-config-service";
import Vue from "vue";
import AppFileInput from "../FileUpload.vue";
import AppWrapper from "./SendMessageDialogWrapper.vue";
import { MailSettings } from "@/models/mail-settings";
import { MailSettingsService } from "@/services/mail-settings-service";
import { ISelectItem } from "winfakt-vue-components";
import AppSelectBccInput from "./SelectBccInput.vue";
import { openBlobInNewTab } from "../../utils/blob";

export default Vue.extend({
	data() {
		return {
			mailData: {
				from: "",
				name: "",
				to: "",
				subject: "",
				body: "",
				attachments: [],
				isHTML: true,
				cc: "",
				bcc: ""
			} as IMail,
			contactInput: "",
			overflowBcc: "",
			sending: false,
			editFrom: false,
			showExtraAddressFields: false,
			selectedMailSettings: null as null | MailSettings,
			allMailSettings: [] as MailSettings[],
		};
	},
	props: {
		title: {
			type: String,
			default: "Nieuw bericht"
		},
		to: String,
		contacts: {
			type: Array as ()=>ISendMessageDialogContact[],
		},
		from: String,
		name: String,
		subject: String,
		content: {
			type: String,
			default: ""
		},
		bcc: {
			type: String,
			default: "",
		},
		additionalBcc: {
			type: String,
			default: "",
			required: false
		},
		cc: {
			type: String,
			default: ""
		},
		attachements: {
			type: Array as () => (File | Blob)[],
			default: () => []
		},
		isHtml: Boolean,
		sendHandler: Function,
		disabled: Boolean,
		mailSettings: {
			type: Object as ()=>MailSettings
		}
	},
	computed: {
		contactList(): ISendMessageDialogContact[]{
			if(!this.contacts){
				return [{
					name: "",
					value: this.mailData.to
				}];
			}
			return this.contacts;
			// Removed filter to function more as a select
			//.filter((c:any) => c.name.toLowerCase().match(this.mailData.to.toLowerCase()) ||  c.value.toLowerCase().match(this.mailData.to.toLowerCase()));
		},
		contactBccList():ISendMessageDialogContact[]{
			let result = [...this.contactList];
			if(this.additionalBcc!=""){
				let c = this.additionalBcc.replaceAll(",", ";").replaceAll(" ", ";").split(";").map(email => ({
					name: "",
					value: email.trim() // Trim to remove extra whitespace, if any
				} as ISendMessageDialogContact));
				result.push(...c);
			}

			// Remove duplicates
			return result.filter((item, i)=>{
				let foundIndex = result.findIndex(a=>a.value == item.value);
				return foundIndex == i;
			});
			// Removed filter to function more as a select
			//.filter((c:any) => c.name.toLowerCase().match(this.mailData.bcc.toLowerCase()) ||  c.value.toLowerCase().match(this.mailData.bcc.toLowerCase()));
		},
		mailFromName(): string {
			if (!this.editFrom) {
				return this.mailData.name + " (" + this.mailData.from + ")";
			}
			return this.mailData.from;
		},
		extraAddressIcon(): string {
			return this.showExtraAddressFields ? "chevron-up" : "chevron-down";
		},
		extraAddressTooltipText(): string {
			return this.showExtraAddressFields ? this.$ct("tooltip.hide-extra-addresses").toString() : this.$ct("tooltip.show-extra-addresses").toString();
		},
		defaultMailSettings():MailSettings{
			let settings = this.allMailSettings.find(f=>f.IsDefault);
			return settings || new MailSettings();
		},
		usedMailSettings():MailSettings{
			if (this.selectedMailSettings){
				return this.selectedMailSettings;
			}
			if (this.mailSettings){
				return this.mailSettings;
			}
			return this.defaultMailSettings;
		},
		showMailSettingsSelect():boolean {
			return this.allMailSettings.length > 1;
		},
		mailSettingsItems():ISelectItem<number>[] {
			return this.allMailSettings.map(m=>{
				return {
					text: m.Label,
					value: m.ID
				} as ISelectItem<number>;
			});
		}
	},
	watch: {
		to(to: string) {
			this.setToAddresses(to);
		},
		from(from: string) {
			this.mailData.from = from;
		},
		name(name: string) {
			this.mailData.name = name;
		},
		subject(subject: string) {
			this.mailData.subject = subject;
		},
		content(content: string) {
			this.mailData.body = `${this.content}\n${
				this.usedMailSettings.Signature
			}`;
		},
		attachements(attachements: (File | Blob)[]) {
			if (attachements) {
				this.mailData.attachments = [...attachements];
			}
		},
		cc(cc:string){
			this.mailData.cc = this.joinAddressLists(this.cc, cc);
		},
		bcc(bcc: string) {
			this.mailData.bcc = this.joinAddressLists(this.bcc, bcc, this.overflowBcc);
		},
		isHtml(isHtml: boolean) {
			this.mailData.isHTML = isHtml;
		},
		mailData: {
			deep: true,
			handler() {
				this.$emit("mail-data-change", this.mailData);
			}
		},
		mailSettings: {
			handler(){
				this.selectedMailSettings = null;
			}
		}
	},
	methods: {
		setToAddresses(to:string){
			let previousOverflow = splitMails(this.overflowBcc);
			let currentBcc = splitMails(this.mailData.bcc).filter(b=>previousOverflow.indexOf(b) == -1);
			this.overflowBcc = "";
			if (DossierConfigService.getGeneralConfig().MailSendAllRecipientsAsTo) {
				this.mailData.to = to;
				this.contactInput = to;
				this.mailData.bcc = currentBcc.join(";");
				return;
			}
			let addresses = splitMails(to);
			let first = addresses[0] || "";
			this.mailData.to = first;
			this.contactInput = first;
			this.overflowBcc = addresses.slice(1).join(";");
			this.mailData.bcc = this.joinAddressLists(currentBcc.join(";"), this.overflowBcc);
		},
		joinAddressLists(...addressLists:string[]):string{
			let result:string[] = [];
			for (let list of addressLists){
				result.push(...list.split(";"));
			}
			result = result.filter((r,i)=>{
				if (!r) return false;
				if (result.indexOf(r) != i) return false;
				return true;
			});
			return result.join(";");
		},
		setSelectedMailSettings(id:number){
			for (let item of this.allMailSettings){
				if (item.ID == id) {
					this.selectedMailSettings = item;
					this.mailData.from = item.EmailFrom;
					this.mailData.from = this.usedMailSettings.EmailFrom || this.usedMailSettings.Email;
					this.mailData.name = this.usedMailSettings.EmailName;
					this.mailData.bcc = this.joinAddressLists(this.usedMailSettings.DefaultBCCEmail, this.bcc, this.overflowBcc);
					this.mailData.cc = this.joinAddressLists(this.usedMailSettings.DefaultCCEmail, this.cc);
					this.mailData.body = `${this.content}\n${
						this.usedMailSettings.Signature
					}`;
					return;
				}
			}
		},
		contactSelected(contact:any){
			let toInput = this.$refs.toInput as Vue;
			let wfInput = toInput.$refs.inputElement as Vue;
			let inputElement = wfInput.$refs.inputElement as HTMLInputElement;
			let currentValue = inputElement.value || "";
			if (currentValue.indexOf(",") != -1 || currentValue.indexOf(";") != -1){
				let values = currentValue.replaceAll(",", ";").split(";");
				let charIndex = 0;
				let selectionIndex = inputElement.selectionStart || this.contactInput.length;
				for (let i = 0; i < values.length; i++){
					let value = values[i];
					let endIndex = charIndex + value.length+1;
					if (selectionIndex >= i && selectionIndex <= endIndex){
						values[i] = contact.value;
						break;
					}
					charIndex = endIndex;
				}
				this.contactInput = values.join(";");
				this.mailData.to = values.join(";");
			}else{
				this.contactInput = contact.value;
				this.mailData.to = contact.value;

			}
		},
		async send() {
			this.sending = true;
			try {
				if (this.sendHandler) {
					await this.sendHandler(this.mailData, this.usedMailSettings);
				} else {
					await MessagesService.sendMail(this.mailData, this.usedMailSettings);
				}
				this.$emit("sent", this.mailData);
				//this.close();
				this.$wf.notify(
					this.$ct("success"),
					"success",
					3500,
					"exclamation-triangle"
				);
			} catch (e:any) {
				let text = this.$ct("error.info");
				text += "<pre class='codeblock mt'><b>"+this.$ct("error.technical") +"</b><br>"+e.message.substr(0, 500);+"</pre>";
				await this.$wf.dialog(text, this.$ct("common.Ok"), this.$ct("common.attention"), "warning");
			}
			this.sending = false;
		},
		close() {
			this.$emit("close");
		},
		onFileAdded(file: File) {
			this.mailData.attachments.push(file);
		},
		removeAttachement(index: number) {
			this.mailData.attachments.splice(index, 1);
		},
		getAttachmentName(file:File | Blob) {
			if ((file as File).name) {
				return (file as File).name;
			}
			return "Nameless attachement";
		},
		async downloadAttachment(file:File | Blob) {
			openBlobInNewTab(file);
		},
		async handlerImageAdded(
			file: File,
			Editor: any,
			cursorLocation: number,
			resetUploader: any
		) {
			let awsFile = await AWSFileService.uploadFile(
				"/email-images",
				file
			);
			let url = awsFile.getUrl();
			Editor.insertEmbed(cursorLocation, "image", url);
			resetUploader();
		},
		updateFrom(value: string) {
			if (this.editFrom) {
				this.$emit("update:from", value);
			}
			this.mailData.from = value;
		},
		toggleExtraAddressFields() {
			this.showExtraAddressFields = !this.showExtraAddressFields;
		}
	},
	async mounted() {
		this.allMailSettings = await MailSettingsService.getSettings();
		this.mailData.from = this.from;
		this.mailData.name = this.name;
		this.mailData.subject = this.subject;
		this.mailData.body = `${this.content}\n${
			this.usedMailSettings.Signature
		}`;
		this.mailData.bcc = this.bcc;
		this.mailData.cc = this.cc;
		this.setToAddresses(this.to);
		this.mailData.attachments = this.attachements;
		if (!this.mailData.from || this.mailData.from.match(/^\s*$/)) {
			this.mailData.from = this.usedMailSettings.EmailFrom || this.usedMailSettings.Email;
			this.mailData.name = this.usedMailSettings.EmailName;
		}
		if(this.usedMailSettings.ID){
			this.setSelectedMailSettings(this.usedMailSettings.ID);
		}
	},
	components: {
		AppFileInput,
		VueEditor: () => import("@/utils/vue-editor"),
		AppWrapper,
		AppSelectBccInput
	},
	i18nEx: {
		componentPrefix: "components.SendMessageDialog"
	}
});
